import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import {
    BookOpen,
    Sparkles,
    Bot,
    ChevronRight,
    ChevronDown,
    AlertTriangle,
    CheckCircle,
    Clock,
    Target,
    Shield,
    GitBranch,
    FileSpreadsheet,
    ClipboardCheck,
    BarChart2,
    FileText,
    Zap,
    Search,
    ArrowRight,
    Activity,
    Users,
    TrendingUp,
    Award,
    X,
} from 'lucide-react';
import clsx from 'clsx';

interface GuideSection {
    id: string;
    title: string;
    icon: React.ReactNode;
    subsections?: { id: string; title: string }[];
}

const UserGuidePage: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const [activeSection, setActiveSection] = useState('overview');
    const [expandedSections, setExpandedSections] = useState<string[]>(['ai-assistants', 'workflow']);
    const [searchTerm, setSearchTerm] = useState('');

    const guideSections: GuideSection[] = [
        {
            id: 'overview',
            title: 'Getting Started',
            icon: <BookOpen size={18} />,
            subsections: [
                { id: 'overview-intro', title: 'Welcome to Magnus PFMEA' },
                { id: 'overview-benefits', title: 'Key Benefits' },
                { id: 'overview-quickstart', title: 'Quick Start Guide' },
            ]
        },
        {
            id: 'ai-assistants',
            title: 'AI Assistants',
            icon: <Bot size={18} />,
            subsections: [
                { id: 'ai-magnus', title: 'Magnus AI Assistant' },
                { id: 'ai-ideation', title: 'AI PFMEA Ideation' },
                { id: 'ai-agents', title: 'Specialized AI Agents' },
            ]
        },
        {
            id: 'workflow',
            title: 'AIAG-VDA 7-Step Workflow',
            icon: <Activity size={18} />,
            subsections: [
                { id: 'step1', title: 'Step 1: Planning & Preparation' },
                { id: 'step2', title: 'Step 2: Structure Analysis' },
                { id: 'step3', title: 'Step 3: Function Analysis' },
                { id: 'step4', title: 'Step 4: Failure Analysis' },
                { id: 'step5', title: 'Step 5: Risk Analysis' },
                { id: 'step6', title: 'Step 6: Optimization' },
                { id: 'step7', title: 'Step 7: Documentation' },
            ]
        },
        {
            id: 'features',
            title: 'App Features',
            icon: <Zap size={18} />,
            subsections: [
                { id: 'feature-dashboard', title: 'Dashboard & Metrics' },
                { id: 'feature-processflow', title: 'Process Flow Diagram' },
                { id: 'feature-pfmea', title: 'PFMEA Worksheet' },
                { id: 'feature-controlplan', title: 'Control Plan' },
                { id: 'feature-reports', title: 'Reports & Export' },
            ]
        },
    ];

    const toggleSection = (sectionId: string) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const scrollToSection = (sectionId: string) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Sidebar - Table of Contents */}
            <aside className={clsx(
                'w-72 fixed left-64 top-20 bottom-0 overflow-y-auto border-r p-4',
                isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className={clsx('text-lg font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                            <BookOpen size={20} className="text-primary-500" />
                            User Guide
                        </h2>
                        <button
                            onClick={() => navigate(-1)}
                            className={clsx(
                                'p-2 rounded-lg transition-colors',
                                isDark ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-800'
                            )}
                            title="Close Guide"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <p className="text-sm text-slate-500">Complete documentation</p>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search guide..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={clsx(
                            'w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                            isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'
                        )}
                    />
                </div>

                {/* Navigation */}
                <nav className="space-y-1">
                    {guideSections.map(section => (
                        <div key={section.id}>
                            <button
                                onClick={() => {
                                    toggleSection(section.id);
                                    scrollToSection(section.id);
                                }}
                                className={clsx(
                                    'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                    activeSection === section.id || activeSection.startsWith(section.id)
                                        ? 'bg-primary-500 text-white'
                                        : isDark
                                            ? 'text-slate-300 hover:bg-slate-800'
                                            : 'text-slate-700 hover:bg-slate-100'
                                )}
                            >
                                <span className="flex items-center gap-2">
                                    {section.icon}
                                    {section.title}
                                </span>
                                {section.subsections && (
                                    expandedSections.includes(section.id)
                                        ? <ChevronDown size={16} />
                                        : <ChevronRight size={16} />
                                )}
                            </button>
                            {section.subsections && expandedSections.includes(section.id) && (
                                <div className="ml-6 mt-1 space-y-1">
                                    {section.subsections.map(sub => (
                                        <button
                                            key={sub.id}
                                            onClick={() => scrollToSection(sub.id)}
                                            className={clsx(
                                                'w-full text-left px-3 py-1.5 rounded text-xs transition-colors',
                                                activeSection === sub.id
                                                    ? 'text-primary-500 font-medium'
                                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                            )}
                                        >
                                            {sub.title}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 ml-72 p-8 space-y-12">
                {/* ============================================================ */}
                {/* SECTION: OVERVIEW */}
                {/* ============================================================ */}
                <section id="overview">
                    <div id="overview-intro" className={clsx('p-8 rounded-2xl border', isDark ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-800' : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200')}>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                <Shield className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className={clsx('text-3xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                    Welcome to Magnus PFMEA
                                </h1>
                                <p className="text-lg text-slate-500">AI-Powered Process Failure Mode and Effects Analysis</p>
                            </div>
                        </div>
                        <p className={clsx('text-lg leading-relaxed', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            Magnus PFMEA is a next-generation, AI-first platform that follows the <strong>AIAG-VDA 7-Step PFMEA methodology</strong>.
                            It's designed to help engineering teams identify, assess, and mitigate process risks faster than ever before—while
                            ensuring full compliance with IATF 16949 and customer-specific requirements.
                        </p>
                    </div>

                    {/* Key Benefits */}
                    <div id="overview-benefits" className="mt-8">
                        <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                            <TrendingUp className="text-green-500" />
                            Key Benefits
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { icon: <Clock size={24} />, title: 'Save 60% Time', desc: 'AI auto-generates failure modes, causes, and controls from process descriptions', color: 'blue' },
                                { icon: <AlertTriangle size={24} />, title: 'Reduce Risk', desc: 'Systematic 4M analysis ensures no failure mode is missed', color: 'red' },
                                { icon: <CheckCircle size={24} />, title: 'AIAG-VDA Compliant', desc: 'Built-in 7-step workflow ensures audit readiness', color: 'green' },
                                { icon: <Users size={24} />, title: 'Team Collaboration', desc: 'Role-based access, action tracking, and approval workflows', color: 'purple' },
                                { icon: <Sparkles size={24} />, title: 'AI Assistants', desc: '6 specialized AI agents for every step of the PFMEA process', color: 'amber' },
                                { icon: <Target size={24} />, title: 'Zero Blank Page', desc: 'Never start from scratch—AI generates initial content', color: 'cyan' },
                            ].map((benefit, idx) => (
                                <div
                                    key={idx}
                                    className={clsx(
                                        'p-5 rounded-xl border-l-4 transition-all hover:shadow-lg',
                                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
                                        `border-l-${benefit.color}-500`
                                    )}
                                    style={{ borderLeftColor: benefit.color === 'blue' ? '#3B82F6' : benefit.color === 'red' ? '#EF4444' : benefit.color === 'green' ? '#10B981' : benefit.color === 'purple' ? '#8B5CF6' : benefit.color === 'amber' ? '#F59E0B' : '#06B6D4' }}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className={clsx('p-2 rounded-lg', isDark ? 'bg-slate-700' : 'bg-slate-100')}>
                                            {benefit.icon}
                                        </div>
                                        <h3 className={clsx('font-bold', isDark ? 'text-white' : 'text-slate-800')}>{benefit.title}</h3>
                                    </div>
                                    <p className="text-sm text-slate-500">{benefit.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Quick Start */}
                    <div id="overview-quickstart" className="mt-8">
                        <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                            <Zap className="text-amber-500" />
                            Quick Start Guide
                        </h2>
                        <div className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                            <ol className="space-y-4">
                                {[
                                    { step: 1, title: 'Select or Create a Project', desc: 'Go to Projects to select an existing project or create a new one', link: '/projects' },
                                    { step: 2, title: 'Define Your Process Flow', desc: 'Use the Process Flow editor to map out your manufacturing steps', link: '/process-flow' },
                                    { step: 3, title: 'Use AI Ideation', desc: 'Let AI generate initial failure modes from your process description', link: '/ideation' },
                                    { step: 4, title: 'Review & Refine PFMEA', desc: 'Use the PFMEA Worksheet to add details, ratings, and controls', link: '/pfmea-editor' },
                                    { step: 5, title: 'Generate Control Plan', desc: 'Auto-generate Control Plan items from your PFMEA', link: '/control-plan' },
                                ].map(item => (
                                    <li key={item.step} className="flex items-start gap-4">
                                        <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                                            {item.step}
                                        </div>
                                        <div>
                                            <Link to={item.link} className="font-semibold text-primary-500 hover:underline flex items-center gap-1">
                                                {item.title}
                                                <ArrowRight size={14} />
                                            </Link>
                                            <p className="text-sm text-slate-500">{item.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </section>

                {/* ============================================================ */}
                {/* SECTION: AI ASSISTANTS */}
                {/* ============================================================ */}
                <section id="ai-assistants">
                    <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                        <Bot className="text-purple-500" />
                        AI Assistants & Tools
                    </h2>
                    <p className={clsx('text-lg mb-6', isDark ? 'text-slate-300' : 'text-slate-600')}>
                        Magnus PFMEA includes powerful AI tools designed to accelerate your analysis, reduce errors, and ensure nothing is missed.
                    </p>

                    {/* Magnus AI Assistant */}
                    <div id="ai-magnus" className={clsx('p-6 rounded-xl border mb-6', isDark ? 'bg-gradient-to-r from-pink-900/30 to-blue-900/30 border-pink-800' : 'bg-gradient-to-r from-pink-50 to-blue-50 border-pink-200')}>
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center">
                                <Sparkles className="text-white" size={28} />
                            </div>
                            <div>
                                <h3 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>Magnus AI Assistant</h3>
                                <p className="text-slate-500">Always available in the bottom-right corner</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className={clsx('p-4 rounded-lg', isDark ? 'bg-slate-800/50' : 'bg-white/70')}>
                                <h4 className="font-semibold text-blue-600 mb-2">🚀 Basic Mode</h4>
                                <ul className="text-sm text-slate-500 space-y-1">
                                    <li>• Quick answers to PFMEA questions</li>
                                    <li>• Workflow navigation help</li>
                                    <li>• Standard guidance and tips</li>
                                </ul>
                            </div>
                            <div className={clsx('p-4 rounded-lg', isDark ? 'bg-slate-800/50' : 'bg-white/70')}>
                                <h4 className="font-semibold text-purple-600 mb-2">⚡ Prime Mode</h4>
                                <ul className="text-sm text-slate-500 space-y-1">
                                    <li>• Deep analysis with project context</li>
                                    <li>• Intelligent recommendations</li>
                                    <li>• Custom prompts and generation</li>
                                </ul>
                            </div>
                        </div>
                        <div className={clsx('mt-4 p-3 rounded-lg text-sm', isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-white text-slate-600')}>
                            <strong>How it helps:</strong> Ask Magnus anything—"What causes high severity ratings?", "How do I link to Control Plan?", "Suggest failure modes for welding."
                        </div>
                    </div>

                    {/* AI PFMEA Ideation */}
                    <div id="ai-ideation" className={clsx('p-6 rounded-xl border mb-6', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center">
                                <Zap className="text-white" size={28} />
                            </div>
                            <div>
                                <h3 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>AI PFMEA Ideation</h3>
                                <Link to="/ideation" className="text-primary-500 text-sm hover:underline flex items-center gap-1">
                                    Go to Ideation <ArrowRight size={12} />
                                </Link>
                            </div>
                        </div>
                        <p className={clsx('mb-4', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            <strong>Solves the Blank Page Problem.</strong> Simply describe your manufacturing process in plain English,
                            and AI generates a complete set of process steps, failure modes, and 4M categories.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {[
                                { icon: '📝', title: 'Describe Process', desc: 'Enter process description in natural language' },
                                { icon: '🔄', title: 'AI Generates', desc: 'AI creates steps, 4M categories, failure modes' },
                                { icon: '✏️', title: 'Refine & Edit', desc: 'Click any step to refine with more detail' },
                            ].map((step, idx) => (
                                <div key={idx} className={clsx('p-3 rounded-lg text-center', isDark ? 'bg-slate-700/50' : 'bg-slate-50')}>
                                    <div className="text-2xl mb-1">{step.icon}</div>
                                    <div className={clsx('font-medium text-sm', isDark ? 'text-white' : 'text-slate-800')}>{step.title}</div>
                                    <div className="text-xs text-slate-500">{step.desc}</div>
                                </div>
                            ))}
                        </div>
                        <div className={clsx('mt-4 p-3 rounded-lg text-sm flex items-center gap-2', isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700')}>
                            <Clock size={16} />
                            <span><strong>Time Saved:</strong> Generate initial PFMEA content in 2 minutes instead of 2 hours</span>
                        </div>
                    </div>

                    {/* Specialized AI Agents */}
                    <div id="ai-agents" className="space-y-4">
                        <h3 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Specialized AI Agents
                        </h3>
                        <p className="text-slate-500 mb-4">
                            6 expert AI agents, each focused on a specific part of the PFMEA workflow. Access them from the AI Ideation page or Magnus Assistant.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                {
                                    icon: '🔍',
                                    name: 'Process Failure Analysis Agent',
                                    focus: 'Step 4: Failure Analysis',
                                    color: 'red',
                                    helps: [
                                        'Identifies failure modes you might miss',
                                        'Applies 4M analysis (Man, Machine, Material, Method)',
                                        'Traces effect chains from local to customer impact',
                                        'Suggests specific root causes based on industry patterns'
                                    ]
                                },
                                {
                                    icon: '⚠️',
                                    name: 'Process Risk Assessment Agent',
                                    focus: 'Step 5: Risk Analysis',
                                    color: 'amber',
                                    helps: [
                                        'Validates your S-O-D ratings',
                                        'Calculates Action Priority (AP) per AIAG-VDA',
                                        'Challenges arbitrary ratings with data questions',
                                        'Identifies high-risk items needing immediate action'
                                    ]
                                },
                                {
                                    icon: '✅',
                                    name: 'PFMEA Compliance Agent',
                                    focus: 'Steps 1, 2, 3, 7',
                                    color: 'green',
                                    helps: [
                                        'Audits against AIAG-VDA 7-step process',
                                        'Checks IATF 16949 compliance',
                                        'Verifies traceability to Control Plan',
                                        'Identifies documentation gaps before audits'
                                    ]
                                },
                                {
                                    icon: '🏗️',
                                    name: 'Process Flow Analysis Agent',
                                    focus: 'Steps 2, 3',
                                    color: 'blue',
                                    helps: [
                                        'Reviews process flow for missing steps',
                                        'Guides 4M classification of operations',
                                        'Identifies special characteristics placement',
                                        'Suggests error-proofing opportunities'
                                    ]
                                },
                                {
                                    icon: '📋',
                                    name: 'Control Plan Generator Agent',
                                    focus: 'Steps 6, 7',
                                    color: 'emerald',
                                    helps: [
                                        'Creates Control Plan from PFMEA detection controls',
                                        'Determines sample sizes based on severity',
                                        'Generates reaction plan templates',
                                        'Identifies items needing SPC'
                                    ]
                                },
                                {
                                    icon: '📊',
                                    name: 'PFMEA Report Generation Agent',
                                    focus: 'Step 7',
                                    color: 'purple',
                                    helps: [
                                        'Creates executive summaries for management',
                                        'Generates risk Pareto charts',
                                        'Prepares customer submission packages',
                                        'Creates action tracking reports'
                                    ]
                                },
                            ].map((agent, idx) => (
                                <div
                                    key={idx}
                                    className={clsx(
                                        'p-5 rounded-xl border transition-all hover:shadow-lg',
                                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                    )}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-2xl">{agent.icon}</span>
                                        <div>
                                            <h4 className={clsx('font-bold', isDark ? 'text-white' : 'text-slate-800')}>{agent.name}</h4>
                                            <span className={clsx('text-xs px-2 py-0.5 rounded-full', isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')}>
                                                {agent.focus}
                                            </span>
                                        </div>
                                    </div>
                                    <ul className="space-y-1">
                                        {agent.helps.map((help, i) => (
                                            <li key={i} className="text-sm text-slate-500 flex items-start gap-2">
                                                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                                {help}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============================================================ */}
                {/* SECTION: AIAG-VDA WORKFLOW */}
                {/* ============================================================ */}
                <section id="workflow">
                    <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                        <Activity className="text-blue-500" />
                        AIAG-VDA 7-Step Workflow
                    </h2>
                    <p className={clsx('text-lg mb-6', isDark ? 'text-slate-300' : 'text-slate-600')}>
                        Magnus PFMEA follows the official AIAG-VDA FMEA Handbook (2019) methodology.
                        The sidebar menu is organized to guide you through each step in order.
                    </p>

                    <div className="space-y-6">
                        {[
                            {
                                id: 'step1', num: 1, title: 'Planning & Preparation', icon: <Target size={20} />, color: 'blue',
                                apps: ['Project Select', '5T Scoping', 'Linked Data'],
                                description: 'Define project scope, team, and timeline using the 5T framework (inTent, Timing, Team, Tools, Tasks).',
                                delivers: ['Clear project boundaries', 'Team member assignments', 'Requirements traceability']
                            },
                            {
                                id: 'step2', num: 2, title: 'Structure Analysis', icon: <GitBranch size={20} />, color: 'purple',
                                apps: ['Process Flow'],
                                description: 'Create the Process Flow Diagram showing all manufacturing operations, inspections, and decision points.',
                                delivers: ['Visual process map', '4M categorization', 'Operation sequence']
                            },
                            {
                                id: 'step3', num: 3, title: 'Function Analysis', icon: <FileSpreadsheet size={20} />, color: 'green',
                                apps: ['Function Matrix'],
                                description: 'Define what each process step must achieve and link to customer requirements.',
                                delivers: ['Process function definitions', 'Requirement links', 'Special characteristics']
                            },
                            {
                                id: 'step4', num: 4, title: 'Failure Analysis', icon: <AlertTriangle size={20} />, color: 'red',
                                apps: ['PFMEA Worksheet', 'Failure Net', 'AI Suggest'],
                                description: 'Identify all potential failure modes, their effects, and root causes using 4M analysis.',
                                delivers: ['Failure mode catalog', 'Effect chains', 'Root cause analysis']
                            },
                            {
                                id: 'step5', num: 5, title: 'Risk Analysis', icon: <BarChart2 size={20} />, color: 'amber',
                                apps: ['Risk Matrix'],
                                description: 'Rate Severity, Occurrence, and Detection. Calculate Action Priority (AP) per AIAG-VDA.',
                                delivers: ['S-O-D ratings', 'Action Priority', 'Risk ranking']
                            },
                            {
                                id: 'step6', num: 6, title: 'Optimization', icon: <CheckCircle size={20} />, color: 'cyan',
                                apps: ['Action Tracker', 'Approvals'],
                                description: 'Define and track recommended actions for high-priority items. Reduce risk through improvements.',
                                delivers: ['Action plans', 'Owner assignments', 'Due date tracking']
                            },
                            {
                                id: 'step7', num: 7, title: 'Results Documentation', icon: <ClipboardCheck size={20} />, color: 'pink',
                                apps: ['Control Plan', 'Work Instructions', 'Reports'],
                                description: 'Document controls, create Control Plan, and export reports for customers and audits.',
                                delivers: ['Control Plan', 'Work Instructions', 'PDF/Excel reports']
                            },
                        ].map(step => (
                            <div
                                id={step.id}
                                key={step.id}
                                className={clsx(
                                    'p-6 rounded-xl border-l-4',
                                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                )}
                                style={{ borderLeftColor: step.color === 'blue' ? '#3B82F6' : step.color === 'red' ? '#EF4444' : step.color === 'green' ? '#10B981' : step.color === 'purple' ? '#8B5CF6' : step.color === 'amber' ? '#F59E0B' : step.color === 'cyan' ? '#06B6D4' : '#EC4899' }}
                            >
                                <div className="flex items-center gap-4 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white font-bold">
                                        {step.num}
                                    </div>
                                    <div>
                                        <h3 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                            Step {step.num}: {step.title}
                                        </h3>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            {step.apps.map(app => (
                                                <span key={app} className={clsx('text-xs px-2 py-0.5 rounded-full', isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')}>
                                                    {app}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className={clsx('mb-3', isDark ? 'text-slate-300' : 'text-slate-600')}>{step.description}</p>
                                <div className={clsx('p-3 rounded-lg', isDark ? 'bg-slate-700/50' : 'bg-slate-50')}>
                                    <div className="text-sm font-medium text-slate-500 mb-1">Delivers:</div>
                                    <div className="flex flex-wrap gap-2">
                                        {step.delivers.map(item => (
                                            <span key={item} className="text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300 px-2 py-1 rounded">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ============================================================ */}
                {/* SECTION: APP FEATURES */}
                {/* ============================================================ */}
                <section id="features">
                    <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                        <Zap className="text-amber-500" />
                        App Features
                    </h2>

                    {/* Feature Cards */}
                    <div className="space-y-6">
                        <div id="feature-dashboard" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                            <h3 className={clsx('text-xl font-bold mb-3 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                                <BarChart2 className="text-blue-500" /> Dashboard & Metrics
                            </h3>
                            <p className="text-slate-500 mb-3">Real-time visibility into your PFMEA status with actionable metrics.</p>
                            <ul className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                <li>• Total PFMEA items count</li>
                                <li>• Action Priority distribution (H/M/L)</li>
                                <li>• 4M cause category breakdown</li>
                                <li>• Open actions and overdue items</li>
                                <li>• AIAG-VDA step completion progress</li>
                                <li>• Control Plan coverage %</li>
                            </ul>
                        </div>

                        <div id="feature-processflow" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                            <h3 className={clsx('text-xl font-bold mb-3 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                                <GitBranch className="text-purple-500" /> Process Flow Diagram Editor
                            </h3>
                            <p className="text-slate-500 mb-3">Visual drag-and-drop editor for creating process flow diagrams.</p>
                            <ul className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                <li>• Drag-and-drop node placement</li>
                                <li>• Operations, Inspections, Decisions</li>
                                <li>• 4M categorization per step</li>
                                <li>• Special characteristics marking</li>
                                <li>• Export to PNG/SVG</li>
                                <li>• Structure tree view</li>
                            </ul>
                        </div>

                        <div id="feature-pfmea" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                            <h3 className={clsx('text-xl font-bold mb-3 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                                <FileSpreadsheet className="text-green-500" /> PFMEA Worksheet
                            </h3>
                            <p className="text-slate-500 mb-3">Full-featured PFMEA editor with all AIAG-VDA fields.</p>
                            <ul className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                <li>• Process step + function</li>
                                <li>• Failure mode, effects, causes</li>
                                <li>• S-O-D ratings with guidance</li>
                                <li>• Prevention & Detection controls</li>
                                <li>• Action Priority calculation</li>
                                <li>• Recommended actions tracking</li>
                            </ul>
                        </div>

                        <div id="feature-controlplan" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                            <h3 className={clsx('text-xl font-bold mb-3 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                                <ClipboardCheck className="text-cyan-500" /> Control Plan
                            </h3>
                            <p className="text-slate-500 mb-3">Auto-generate Control Plan from PFMEA detection controls.</p>
                            <ul className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                <li>• Auto-populated from PFMEA</li>
                                <li>• Sample size recommendations</li>
                                <li>• Measurement methods</li>
                                <li>• Reaction plans</li>
                                <li>• AIAG format export</li>
                                <li>• Customer-specific templates</li>
                            </ul>
                        </div>

                        <div id="feature-reports" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                            <h3 className={clsx('text-xl font-bold mb-3 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                                <FileText className="text-pink-500" /> Reports & Export
                            </h3>
                            <p className="text-slate-500 mb-3">Comprehensive reporting for management and customers.</p>
                            <ul className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                <li>• Executive summary reports</li>
                                <li>• Risk Pareto analysis</li>
                                <li>• PDF export (customer deliverable)</li>
                                <li>• Excel export (editable)</li>
                                <li>• Action status tracking</li>
                                <li>• Audit-ready documentation</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <div className={clsx('p-6 rounded-xl border text-center', isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200')}>
                    <Award className="mx-auto text-primary-500 mb-3" size={32} />
                    <h3 className={clsx('text-lg font-bold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                        Need More Help?
                    </h3>
                    <p className="text-slate-500 mb-4">
                        Use the Magnus AI Assistant (bottom-right corner) for instant help, or contact support.
                    </p>
                    <Link
                        to="/ai-assistant"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-purple-500 text-white rounded-xl font-medium hover:opacity-90"
                    >
                        <Bot size={18} />
                        Open AI Assistant
                    </Link>
                </div>
            </main>
        </div>
    );
};

export default UserGuidePage;
